'use client';

import { createStore, StoreSnapshot } from '@xstate/store';

export interface ContextStoreProduct {
  products: Record<string, MapProduct>;
}

type MapProduct = {
  wishlist?: { id: string; count: number };
  cart?: { id: string; count: number; loading: boolean; isGift?: boolean };
  compare?: { id: string; count: number };
};

const initialContext: ContextStoreProduct = {
  products: {}
};

export const storeProduct = createStore({
  context: initialContext,
  on: {
    set: (
      context: ContextStoreProduct,
      event: {
        state: Partial<ContextStoreProduct>;
      }
    ) => {
      return {
        ...context,
        ...event.state
      };
    },
    setProduct: (
      context: ContextStoreProduct,
      event: {
        sku: string;
      } & MapProduct
    ) => {
      const currentProduct = context.products[event.sku];
      const wishlist = event?.wishlist ?? currentProduct?.wishlist ?? undefined;
      const cart = event?.cart ?? currentProduct?.cart ?? undefined;
      const compare = event?.compare ?? currentProduct?.compare ?? undefined;
      return {
        ...context,
        products: {
          ...context.products,
          [event.sku]: {
            wishlist,
            cart,
            compare
          }
        }
      };
    },
    setClearedCart: (context: ContextStoreProduct) => {
      return {
        ...context,
        products: Object.entries(context.products).reduce(
          (acc, [sku, product]) => {
            if (sku) {
              acc[sku] = {
                ...product,
                cart: undefined
              };
            }
            return acc;
          },
          {} as Record<string, MapProduct>
        )
      };
    },
    setClearedCompare: (context: ContextStoreProduct) => {
      return {
        ...context,
        products: Object.entries(context.products).reduce(
          (acc, [sku, product]) => {
            if (sku) {
              acc[sku] = {
                ...product,
                compare: undefined
              };
            }
            return acc;
          },
          {} as Record<string, MapProduct>
        )
      };
    },
    setProductCart: (
      context: ContextStoreProduct,
      event: {
        sku: string;
      } & MapProduct
    ) => {
      const currentProduct = context.products[event.sku];
      return {
        ...context,
        products: {
          ...context.products,
          [event.sku]: {
            ...currentProduct,
            cart: event?.cart
          }
        }
      };
    },
    setProductCartLoading: (
      context: ContextStoreProduct,
      event: {
        sku: string;
        loading: boolean;
      }
    ) => {
      const currentProduct = context.products[event.sku];
      return {
        ...context,
        products: {
          ...context.products,
          [event.sku]: {
            ...currentProduct,
            cart: {
              ...currentProduct?.cart,
              loading: event.loading
            } as MapProduct['cart']
          }
        }
      };
    },
    setProductWishlist: (
      context: ContextStoreProduct,
      event: {
        sku: string;
      } & MapProduct
    ) => {
      const currentProduct = context.products[event.sku];
      return {
        ...context,
        products: {
          ...context.products,
          [event.sku]: {
            ...currentProduct,
            wishlist: event?.wishlist
          }
        }
      };
    },
    setProductCompare: (
      context: ContextStoreProduct,
      event: {
        sku: string;
      } & MapProduct
    ) => {
      const currentProduct = context.products[event.sku];
      return {
        ...context,
        products: {
          ...context.products,
          [event.sku]: {
            ...currentProduct,
            compare: event?.compare
          }
        }
      };
    }
  }
});

if (typeof window != 'undefined') {
  const tabId = Math.random().toString(36).slice(2);
  let isReceivingBroadcast = false;

  const channel = new BroadcastChannel('storeProduct');

  function sendMessage(msg: ContextStoreProduct) {
    channel.postMessage({ senderId: tabId, message: msg });
  }

  storeProduct.inspect((payload) => {
    if (
      payload.type === '@xstate.snapshot' &&
      !isReceivingBroadcast &&
      'context' in payload.snapshot
    ) {
      sendMessage(payload.snapshot.context as ContextStoreProduct);
    }
  });

  channel.onmessage = (event) => {
    if (event.data.senderId === tabId) {
      // Игнорируем сообщение от самого себя
      return;
    }

    isReceivingBroadcast = true;
    storeProduct.send({
      type: 'set',
      state: event.data.message
    });
    isReceivingBroadcast = false;
  };
}

export const selectCountProductInWishlist =
  (sku?: string) => (state: StoreSnapshot<ContextStoreProduct>) => {
    if (!sku) {
      return 0;
    }

    const product = state.context.products[sku];
    return product?.wishlist?.count ?? 0;
  };

export const selectCountProductInCart =
  (sku?: string) => (state: StoreSnapshot<ContextStoreProduct>) => {
    if (!sku) {
      return 0;
    }

    const product = state.context.products[sku];
    return product?.cart?.count ?? 0;
  };

export const selectWishlistItemId =
  (sku?: string) => (state: StoreSnapshot<ContextStoreProduct>) => {
    if (!sku) {
      return undefined;
    }

    const product = state.context.products[sku];
    return product?.wishlist?.id;
  };

export const selectCartItemId =
  (sku?: string) => (state: StoreSnapshot<ContextStoreProduct>) => {
    if (!sku) {
      return '';
    }

    const product = state.context.products[sku];
    return product?.cart?.id || '';
  };

export const selectCompareProducts =
  (sku?: string) => (state: StoreSnapshot<ContextStoreProduct>) => {
    if (!sku) {
      return '';
    }

    const product = state.context.products[sku];
    return product?.compare?.id || '';
  };

export const selectCartItemLoading =
  (sku?: string) => (state: StoreSnapshot<ContextStoreProduct>) => {
    if (!sku) {
      return false;
    }

    const product = state.context.products[sku];
    return product?.cart?.loading || false;
  };

export const selectCounterWishlist = (
  state: StoreSnapshot<ContextStoreProduct>
) => {
  return Object.values(state.context.products).filter(
    (product) => product.wishlist?.count
  ).length;
};

export const selectCounterCart = (
  state: StoreSnapshot<ContextStoreProduct>
) => {
  return Object.values(state.context.products).reduce((total, product) => {
    // Исключаем подарки из подсчета
    if (product.cart?.isGift) {
      return total;
    }
    return total + (product.cart?.count || 0);
  }, 0);
};

export const selectCounterCompare = (
  state: StoreSnapshot<ContextStoreProduct>
) => {
  return Object.values(state.context.products).filter(
    (product) => product.compare?.count
  ).length;
};
